"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
  ProductSortDropdown,
  ProductsFilter,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Loader, SlidersHorizontal } from "lucide-react";


function ProductsGridConsumer() {
  const { products, loading, error, hasNext, loadMore, isRefetching } = useEcommerceProducts();

  if (loading && products.length === 0) {
    return (
      <div data-section-id="163855" className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} data-index={i} className="flex flex-col gap-4">
            <div className="aspect-[4/5] w-full animate-pulse rounded-xl bg-muted/50" />
            <div className="space-y-2">
              <div className="h-4 w-2/3 animate-pulse rounded bg-muted/50" />
              <div className="h-4 w-1/3 animate-pulse rounded bg-muted/50" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border border-dashed border-border p-8 text-center bg-card/40 backdrop-blur-sm">
        <p className="mb-4 font-sans text-muted-foreground">Unable to retrieve the latest catch.</p>
        <Button variant="outline" onClick={() => window.location.reload()} className="font-sans border-primary text-primary hover:bg-primary/10">
          Try Again
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border border-dashed border-border p-8 text-center bg-card/40 backdrop-blur-sm">
        <p className="font-serif text-xl font-medium text-foreground">No seafood currently in stock</p>
        <p className="font-sans text-sm text-muted-foreground mt-2">Try adjusting your selection criteria or check back soon.</p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => (
          <article
            key={product.id}
            data-index={i}
            className="group relative flex flex-col overflow-hidden rounded-xl bg-card transition-all hover:shadow-xl hover:shadow-primary/5"
          >
            <Link to={product.link} className="absolute inset-0 z-10">
              <span className="sr-only">View {product.title}</span>
            </Link>

            <div className="aspect-[4/5] w-full overflow-hidden bg-muted">
              {product.featured_image_url ? (
                <img
                  src={product.featured_image_url}
                  alt={product.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-secondary/20 text-secondary font-serif italic">
                  Village Seafood
                </div>
              )}
            </div>

            <div className="flex flex-1 flex-col justify-between p-6">
              <div className="space-y-1">
                <h3 className="font-serif text-xl font-bold tracking-tight text-primary line-clamp-1">
                  {product.title}
                </h3>
                {product.price !== undefined && (
                  <p className="font-sans text-sm font-semibold text-muted-foreground tracking-wide">
                    ${product.price.toFixed(2)}
                  </p>
                )}
              </div>

              <div className="mt-6">
                <AddToCartButton
                  productId={product.id}
                  variant="default"
                  className="w-full relative z-20"
                  buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-sans tracking-wide transition-all duration-300"
                  text="Order Fresh"
                  showQuantity={false}
                />
              </div>
            </div>
          </article>
        ))}
      </div>

      {hasNext && (
        <div className="flex justify-center pt-8">
          <Button
            variant="outline"
            size="lg"
            onClick={async () => await loadMore()}
            disabled={isRefetching}
            className="min-w-[220px] font-sans border-primary text-primary hover:bg-primary/5 rounded-full"
          >
            {isRefetching ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              "View More Selections"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ShopContent() {
  
  const current_wp_query_params = typeof window !== 'undefined' && (window as any).wvcClient 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : {};
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section id="shop-content" className="bg-background text-foreground w-full">
      <EcommerceProductsProvider wp_query={wp_query}>
        <SidebarProvider>
          {}
          <Sidebar variant="sidebar" className="border-r border-border bg-sidebar pt-16 md:pt-0">
            <SidebarHeader className="px-6 py-8">
              <h2 className="flex items-center gap-2 font-serif text-2xl font-bold tracking-tight text-primary">
                <SlidersHorizontal className="h-5 w-5 text-secondary" />
                Refine Catch
              </h2>
            </SidebarHeader>
            <SidebarContent className="px-6 pb-10">
              <ProductsFilter />
            </SidebarContent>
          </Sidebar>

          {}
          <SidebarInset className="bg-background">
            <header className="sticky top-0 z-30 flex h-20 shrink-0 items-center justify-between gap-4 border-b border-border bg-background/80 px-4 backdrop-blur-md md:px-10">
              <div className="flex items-center gap-4">
                <SidebarTrigger className="md:hidden text-primary" />
                <h1 className="font-serif text-2xl font-bold tracking-tight text-primary md:text-3xl">
                  Market Selection
                </h1>
              </div>
              <div className="flex items-center gap-4">
                <ProductSortDropdown label="Sort Selection" />
              </div>
            </header>

            <main className="flex-1 p-6 md:p-10">
              <ProductsGridConsumer />
            </main>
          </SidebarInset>
        </SidebarProvider>
      </EcommerceProductsProvider>
    </section>
  );
}